import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SocialMediaSettings = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchConnectedAccounts();
    
    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const oauthCallback = urlParams.get('oauth');
    
    if (code && oauthCallback === 'callback') {
      handleOAuthCallback(code, state);
    }
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/social-media/accounts', config);
      setAccounts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Failed to load connected accounts');
      setAccounts([]); // Ensure accounts is always an array
    }
  };

  const handleConnectFacebook = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/social-media/oauth/auth-url', config);
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      setError('Failed to initiate Facebook connection');
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (code, state) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.post('/api/social-media/oauth/callback', {
        code,
        state
      }, config);
      
      setSuccess(`Successfully connected ${response.data.connectedPages} Facebook page(s)`);
      fetchConnectedAccounts();
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname + '?tab=socialMedia');
    } catch (error) {
      console.error('OAuth callback error:', error);
      setError(error.response?.data?.message || 'Failed to connect Facebook pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/social-media/accounts/${accountId}`, config);
      setSuccess('Account disconnected successfully');
      fetchConnectedAccounts();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      setError('Failed to disconnect account');
    }
  };

  const handleSyncData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.post('/api/social-media/fetch-all-data', {
        startDate,
        endDate
      }, config);
      
      setSuccess(`Data sync completed! Fetched ${response.data.results.posts} posts with engagement metrics`);
    } catch (error) {
      console.error('Error syncing data:', error);
      setError('Failed to sync social media data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Social Media Integration</h3>
          <p className="mt-1 text-sm text-gray-500">
            Connect your Facebook and Instagram accounts to track social media performance
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}

          {/* Connect New Account */}
          <div className="mb-8">
            <h4 className="text-md font-medium text-gray-900 mb-4">Connect New Account</h4>
            <div className="flex space-x-4">
              <button
                onClick={handleConnectFacebook}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Connecting...
                  </>
                ) : (
                  <>
                    📘 Connect Facebook
                  </>
                )}
              </button>
              
              <button
                disabled
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed"
              >
                📷 Connect Instagram (Coming Soon)
              </button>
            </div>
          </div>

          {/* Connected Accounts */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-md font-medium text-gray-900">Connected Accounts</h4>
              {accounts.length > 0 && (
                <button
                  onClick={handleSyncData}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Syncing...' : '🔄 Sync Data'}
                </button>
              )}
            </div>

            {!accounts || accounts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No accounts connected yet.</p>
                <p className="text-sm mt-2">Connect your Facebook or Instagram account to get started.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {(accounts || []).map((account) => (
                  <div key={account.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">
                          {account.platform === 'facebook' ? '📘' : '📷'}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-900">{account.page_name}</h5>
                          <p className="text-sm text-gray-500">
                            {account.platform === 'facebook' ? 'Facebook Page' : 'Instagram Account'}
                          </p>
                          {account.page_username && (
                            <p className="text-xs text-gray-400">@{account.page_username}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Connected
                        </span>
                        <button
                          onClick={() => handleDisconnectAccount(account.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h5 className="text-sm font-medium text-blue-800">
                  How it works
                </h5>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Connect your Facebook or Instagram business accounts</li>
                    <li>We'll automatically sync your posts and engagement metrics</li>
                    <li>View social media performance in your KPI dashboard</li>
                    <li>Track likes, comments, shares, and reach over time</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaSettings;